// GSTR-2B API Service
import { GSTR2BRecord, GSTR2BStats, ReconciliationSummary, AvailableInvoicesResponse } from '../types/gstr2bTypes';
import { API_BASE_URL } from '../config/api';

// API Response Types
interface APIResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

interface ImportResponse {
  upload_id: number;
  return_period: string;
  gstin: string;
  stats: {
    total_suppliers: number;
    total_invoices: number;
    new_invoices: number;
    duplicate_invoices: number;
    total_itc: number;
  };
}

interface InvoicesResponse {
  invoices: GSTR2BRecord[];
  summary: GSTR2BStats;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface ReturnPeriod {
  value: string;
  label: string;
  gstin: string;
  upload_date: string;
  total_invoices: number;
  total_itc: number;
}

interface ReconciliationDataResponse {
  invoices: GSTR2BRecord[];
  summary: ReconciliationSummary;
}

/** Single row from GSTR-2B upload log (backend returns snake_case) */
export interface UploadLogEntry {
  id: number;
  company_id: number;
  gstin: string;
  return_period: string;
  generation_date: string | null;
  upload_date: string;
  uploaded_by: number | null;
  checksum: string | null;
  file_name: string | null;
  s3_bucket: string | null;
  s3_key: string | null;
  total_suppliers: number;
  total_invoices: number;
  new_invoices: number;
  duplicate_invoices: number;
  total_itc_amount: string | number;
  json_version: string | null;
  status: 'processing' | 'completed' | 'failed';
  error_message: string | null;
}

interface UploadLogResponse {
  uploads: UploadLogEntry[];
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('access_token');
  return token;
};

// Helper function to create headers
const createHeaders = (includeContentType: boolean = true): HeadersInit => {
  const headers: HeadersInit = {
    Authorization: `Bearer ${getAuthToken()}`,
  };

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

// GSTR-2B API functions
export const gstr2bAPI = {
  /**
   * Import GSTR-2B JSON file
   */
  importJSON: async (file: File): Promise<APIResponse<ImportResponse>> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/api/gstr2b/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to import GSTR-2B JSON');
      }

      return data;
    } catch (error: any) {
      console.error('GSTR-2B Import Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to import GSTR-2B JSON',
      };
    }
  },

  /**
   * Get upload log (history of GSTR-2B file uploads)
   */
  getUploadLog: async (): Promise<APIResponse<UploadLogResponse>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gstr2b/upload-log`, {
        method: 'GET',
        headers: createHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch upload log');
      }
      return data;
    } catch (error: any) {
      console.error('GSTR-2B Upload Log Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch upload log',
      };
    }
  },

  resetUploadPeriod: async (uploadId: number): Promise<APIResponse<{
    upload_id: number;
    return_period: string;
    invoices_deleted: number;
  }>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gstr2b/upload-log/${uploadId}`, {
        method: 'DELETE',
        headers: createHeaders(),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to reset period');
      return data;
    } catch (error: any) {
      return { success: false, message: error.message };
    }
  },

  getDownloadUrl: async (uploadId: number): Promise<APIResponse<{ download_url: string; file_name: string }>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gstr2b/download/${uploadId}`, {
        method: 'GET',
        headers: createHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to get download URL');
      }
      return data;
    } catch (error: any) {
      console.error('GSTR-2B Download Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to get download URL',
      };
    }
  },

  resetAll: async (): Promise<APIResponse<{ invoices_deleted: number; uploads_deleted: number }>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gstr2b/reset`, {
        method: 'POST',
        headers: createHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset GSTR-2B data');
      }
      return data;
    } catch (error: any) {
      console.error('GSTR-2B Reset Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to reset GSTR-2B data',
      };
    }
  },

  /**
   * Fetch GSTR-2B invoices with filters
   */
  fetchInvoices: async (params: {
    return_period?: string;
    gstin?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<APIResponse<InvoicesResponse>> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.return_period) queryParams.append('return_period', params.return_period);
      if (params.gstin) queryParams.append('gstin', params.gstin);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());

      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/invoices?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: createHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch invoices');
      }

      // Transform backend data to match frontend interface
      if (data.success && data.data) {
        // Map snake_case to camelCase for stats
        const summary = data.data.summary;
        data.data.summary = {
          totalInvoices: summary.total_invoices || 0,
          totalITCBooks: summary.total_itc || 0,
          reconciledCount: summary.matched_count || 0,
          issuesCount: summary.issues_count || 0,
        };

        // Map invoice data from snake_case to camelCase
        if (data.data.invoices && Array.isArray(data.data.invoices)) {
          data.data.invoices = data.data.invoices.map((inv: any) => ({
            id: inv.id,
            invoiceNumber: inv.invoice_number,
            invoiceDate: inv.invoice_date,
            supplierName: inv.supplier_name,
            supplierGSTIN: inv.supplier_gstin,
            booksAmount: parseFloat(inv.invoice_value) || 0,
            gstr2bAmount: parseFloat(inv.invoice_value) || 0,
            taxAmount: parseFloat(inv.sgst_amount || 0) + parseFloat(inv.cgst_amount || 0) + 
                       parseFloat(inv.igst_amount || 0) + parseFloat(inv.cess_amount || 0),
            status: inv.reconciliation_status || 'pending',
            returnPeriod: inv.return_period,
            gstin: inv.recipient_gstin,
          }));
        }
      }

      return data;
    } catch (error: any) {
      console.error('Fetch Invoices Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch invoices',
      };
    }
  },

  /**
   * Get available return periods
   */
  fetchReturnPeriods: async (): Promise<APIResponse<ReturnPeriod[]>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gstr2b/return-periods`, {
        method: 'GET',
        headers: createHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch return periods');
      }

      return data;
    } catch (error: any) {
      console.error('Fetch Return Periods Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch return periods',
      };
    }
  },

  /**
   * Get available GSTINs
   */
  fetchGSTINs: async (): Promise<APIResponse<string[]>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gstr2b/gstins`, {
        method: 'GET',
        headers: createHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch GSTINs');
      }

      return data;
    } catch (error: any) {
      console.error('Fetch GSTINs Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch GSTINs',
      };
    }
  },

  /**
   * Fetch reconciliation data for a specific period, from-to range, or all periods
   * @param params.return_period - Single period (MMYYYY)
   * @param params.from_period - Start of range (MMYYYY); use with to_period
   * @param params.to_period - End of range (MMYYYY); use with from_period
   */
  fetchReconciliationData: async (params: {
    return_period?: string;
    from_period?: string;
    to_period?: string;
    /** ZOUR-91 — OUR recipient registration. Omitted = every registration. */
    gstin?: string;
  }): Promise<APIResponse<ReconciliationDataResponse>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.return_period) queryParams.append('return_period', params.return_period);
      if (params.from_period) queryParams.append('from_period', params.from_period);
      if (params.to_period) queryParams.append('to_period', params.to_period);
      if (params.gstin) queryParams.append('gstin', params.gstin);

      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/reconciliation-data?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: createHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch reconciliation data');
      }

      // Transform backend data to match frontend interface
      if (data.success && data.data) {
        // Map snake_case to camelCase for summary
        const summary = data.data.summary;
        data.data.summary = {
          totalPurchaseEntries: summary.total_purchase_entries || 0,
          total2BInvoices: summary.total_2b_invoices || 0,
          matchedCount: summary.matched_count || 0,
          suggestiveMatchesCount: summary.suggestive_matches_count || 0,
          missingInBooksCount: summary.missing_in_books_count || 0,
          missingIn2BCount: summary.missing_in_2b_count || 0,
          mismatchCount: summary.mismatch_count || 0,
          excludedCount: summary.excluded_count || 0,
          pendingCount: summary.pending_count || 0,
          hasNewBillsForRecheck: summary.has_new_bills_for_recheck || false,
        };

        // Map invoice data from snake_case to camelCase
        if (data.data.invoices && Array.isArray(data.data.invoices)) {
          data.data.invoices = data.data.invoices.map((inv: any) => ({
            id: inv.id,
            // GSTR-2B fields
            invoiceNumber: inv.invoice_number,
            invoiceDate: inv.invoice_date,
            supplierName: inv.supplier_name,
            supplierGSTIN: inv.supplier_gstin,
            gstr2bTaxableValue: parseFloat(inv.gstr2b_taxable_value) || 0,
            gstr2bCGST: parseFloat(inv.gstr2b_cgst) || 0,
            gstr2bSGST: parseFloat(inv.gstr2b_sgst) || 0,
            gstr2bIGST: parseFloat(inv.gstr2b_igst) || 0,
            gstr2bCess: parseFloat(inv.gstr2b_cess) || 0,
            gstr2bAmount: parseFloat(inv.gstr2b_amount) || 0,
            // Books fields
            booksInvoiceNumber: inv.books_invoice_number,
            booksInvoiceDate: inv.books_invoice_date,
            booksTaxableValue: parseFloat(inv.books_taxable_value) || 0,
            booksCGST: parseFloat(inv.books_cgst) || 0,
            booksSGST: parseFloat(inv.books_sgst) || 0,
            booksIGST: parseFloat(inv.books_igst) || 0,
            booksAmount: parseFloat(inv.books_amount) || 0,
            // Other fields
            status: inv.reconciliation_status || 'pending',
            returnPeriod: inv.return_period,
            gstin: inv.recipient_gstin,
            matchedBillId: inv.matched_bill_id || null,
            exclusionReason: inv.exclusion_reason || null,
            // Match metadata: how/when/who the match was made
            matchType: inv.match_type || null,
            reconciledAt: inv.reconciled_at || null,
            matchedByName: inv.matched_by_name || null,
          }));
        }
      }

      return data;
    } catch (error: any) {
      console.error('Fetch Reconciliation Data Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch reconciliation data',
      };
    }
  },

  /**
   * Mark a suggestive match as verified
   */
  markAsVerified: async (invoiceId: string, notes?: string): Promise<APIResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/mark-verified/${invoiceId}`,
        {
          method: 'PUT',
          headers: createHeaders(),
          body: JSON.stringify({
            notes: notes || 'Manual verification via UI'
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to mark invoice as verified');
      }

      return data;
    } catch (error: any) {
      console.error('Mark as Verified Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to mark invoice as verified',
      };
    }
  },

  getInvoiceLogs: async (invoiceId: string): Promise<APIResponse<{ logs: Array<{
    id: number;
    action: string;
    from_status: string | null;
    to_status: string | null;
    matched_bill_id: number | null;
    notes: string | null;
    performed_by: number | null;
    created_at: string;
  }> }>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gstr2b/logs/${invoiceId}`, {
        method: 'GET',
        headers: createHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch logs');
      }
      return data;
    } catch (error: any) {
      return { success: false, message: error.message || 'Failed to fetch logs' };
    }
  },

  unmatchInvoice: async (invoiceId: string): Promise<APIResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/unmatch/${invoiceId}`,
        {
          method: 'PUT',
          headers: createHeaders(),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to unmatch invoice');
      }
      return data;
    } catch (error: any) {
      console.error('Unmatch Invoice Error:', error);
      return { success: false, message: error.message || 'Failed to unmatch invoice' };
    }
  },

  excludeInvoice: async (invoiceId: string, reason: string): Promise<APIResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/exclude/${invoiceId}`,
        {
          method: 'PUT',
          headers: createHeaders(),
          body: JSON.stringify({ reason }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to exclude invoice');
      }
      return data;
    } catch (error: any) {
      console.error('Exclude Invoice Error:', error);
      return { success: false, message: error.message || 'Failed to exclude invoice' };
    }
  },

  excludePurchaseRegisterEntry: async (prId: number, reason: string): Promise<APIResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/exclude-pr/${prId}`,
        {
          method: 'PUT',
          headers: createHeaders(),
          body: JSON.stringify({ reason }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to exclude entry');
      }
      return data;
    } catch (error: any) {
      console.error('Exclude PR Entry Error:', error);
      return { success: false, message: error.message || 'Failed to exclude entry' };
    }
  },

  restorePurchaseRegisterEntry: async (prId: number): Promise<APIResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/restore-pr/${prId}`,
        {
          method: 'PUT',
          headers: createHeaders(),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to restore entry');
      }
      return data;
    } catch (error: any) {
      console.error('Restore PR Entry Error:', error);
      return { success: false, message: error.message || 'Failed to restore entry' };
    }
  },

  restoreInvoice: async (invoiceId: string): Promise<APIResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/restore/${invoiceId}`,
        {
          method: 'PUT',
          headers: createHeaders(),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to restore invoice');
      }
      return data;
    } catch (error: any) {
      console.error('Restore Invoice Error:', error);
      return { success: false, message: error.message || 'Failed to restore invoice' };
    }
  },

  /**
   * Get available invoices for manual matching
   */
  getAvailableInvoices: async (gstr2bInvoiceId: string): Promise<APIResponse<AvailableInvoicesResponse>> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/available-invoices/${gstr2bInvoiceId}`,
        {
          method: 'GET',
          headers: createHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch available invoices');
      }

      // Transform backend data to match frontend interface
      if (data.success && data.data) {
        const transformed = {
          gstr2bInvoice: {
            id: data.data.gstr2b_invoice.id,
            invoiceNumber: data.data.gstr2b_invoice.invoice_number,
            invoiceDate: data.data.gstr2b_invoice.invoice_date,
            invoiceValue: data.data.gstr2b_invoice.invoice_value,
            taxableValue: parseFloat(data.data.gstr2b_invoice.taxable_value) || 0,
            cgstAmount: parseFloat(data.data.gstr2b_invoice.cgst_amount) || 0,
            sgstAmount: parseFloat(data.data.gstr2b_invoice.sgst_amount) || 0,
            igstAmount: parseFloat(data.data.gstr2b_invoice.igst_amount) || 0,
            cessAmount: parseFloat(data.data.gstr2b_invoice.cess_amount) || 0,
            supplierGSTIN: data.data.gstr2b_invoice.supplier_gstin,
            supplierName: data.data.gstr2b_invoice.supplier_name,
          },
          availableInvoices: data.data.available_invoices.map((inv: any) => ({
            id: inv.id,
            invoiceNumber: inv.invoice_number,
            invoiceDate: inv.invoice_date,
            vendorName: inv.vendor_name,
            vendorGSTIN: inv.vendor_gstin,
            taxableAmount: parseFloat(inv.taxable_amount) || 0,
            cgstAmount: parseFloat(inv.cgst_amount) || 0,
            sgstAmount: parseFloat(inv.sgst_amount) || 0,
            igstAmount: parseFloat(inv.igst_amount) || 0,
            totalAmount: parseFloat(inv.total_amount) || 0,
            amountDifference: parseFloat(inv.amount_difference) || 0,
          })),
          count: data.data.count,
        };

        return {
          success: true,
          data: transformed,
        };
      }

      return data;
    } catch (error: any) {
      console.error('Get Available Invoices Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch available invoices',
      };
    }
  },

  /**
   * Create manual match between GSTR-2B and purchase register invoice
   */
  createManualMatch: async (
    gstr2bInvoiceId: string,
    purchaseRegisterId: string,
    notes?: string
  ): Promise<APIResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/manual-match`,
        {
          method: 'PUT',
          headers: createHeaders(),
          body: JSON.stringify({
            gstr2b_invoice_id: gstr2bInvoiceId,
            purchase_register_id: purchaseRegisterId,
            notes: notes || 'Manual match via UI',
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create manual match');
      }

      return data;
    } catch (error: any) {
      console.error('Create Manual Match Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to create manual match',
      };
    }
  },

  /**
   * ZOUR-91 — `gstin` is OUR recipient registration, not the supplier's.
   *
   * GSTR-2B is generated per GSTIN, so reconciliation is a per-registration
   * operation. The endpoint has always accepted this parameter; the page never
   * sent it, so every registration's 2B rows were reconciled against one
   * undifferentiated pool of books in a single run.
   *
   * Omitted = every registration, which is what it did before and what a
   * single-registration client still gets.
   */
  runReconciliation: async (gstin?: string): Promise<APIResponse<{
    total_processed: number;
    results: {
      matched: number;
      suggestive_invoice_mismatch: number;
      suggestive_date_mismatch: number;
      suggestive_amount_mismatch: number;
      missing_in_books: number;
    };
  }>> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/run-reconciliation${gstin ? `?gstin=${encodeURIComponent(gstin)}` : ''}`,
        { method: 'GET', headers: createHeaders() }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to run reconciliation');
      return data;
    } catch (error: any) {
      return { success: false, message: error.message };
    }
  },

  /**
   * Re-run reconciliation over invoices previously marked "missing in books".
   * Picks up bills that were uploaded after the original 2B sync ran.
   */
  reconcileMissingInBooks: async (gstin?: string): Promise<APIResponse<{
    total_processed: number;
    results: {
      matched: number;
      suggestive_invoice_mismatch: number;
      suggestive_date_mismatch: number;
      suggestive_amount_mismatch: number;
      missing_in_books: number;
    };
  }>> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/reconcile-missing-in-books${gstin ? `?gstin=${encodeURIComponent(gstin)}` : ''}`,
        { method: 'GET', headers: createHeaders() }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to re-check missing-in-books');
      return data;
    } catch (error: any) {
      return { success: false, message: error.message };
    }
  },

  getPrCount: async (fromMonth: string, toMonth: string): Promise<APIResponse<{
    count: number;
    date_from: string;
    date_to: string;
  }>> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/pr-count?from_month=${fromMonth}&to_month=${toMonth}`,
        { method: 'GET', headers: createHeaders() }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to get PR count');
      return data;
    } catch (error: any) {
      return { success: false, message: error.message };
    }
  },

  /**
   * Sync bills from Zoho Books and process to purchase register in one step
   */
  syncZohoBills: async (fromMonth: string, toMonth: string): Promise<APIResponse<{
    bills_synced: number;
    bills_updated_in_staging: number;
    bills_processed: number;
    bills_skipped_no_gstin: number;
    purchase_register_entries_created: number;
    pr_replaced: number;
    pr_unreconciled: number;
    pr_unreconcile_details: Array<{
      bill_id: string;
      vendor_name: string;
      invoice_no: string;
      old_amount: number;
      new_amount: number;
    }>;
    errors: Array<{ bill_id: string; error: string }>;
    from_month: string;
    to_month: string;
    date_from: string;
    date_to: string;
  }>> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/sync-zoho-bills`,
        {
          method: 'POST',
          headers: createHeaders(),
          body: JSON.stringify({
            from_month: fromMonth,
            to_month: toMonth,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to sync bills from Zoho');
      }

      return data;
    } catch (error: any) {
      console.error('Sync Zoho Bills Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to sync bills from Zoho',
      };
    }
  },

  /**
   * Download Excel export for a specific category
   * @param category - matched, suggested, missing_in_books, missing_in_2b, all_pending, all
   * @param returnPeriod - Optional single return period (MMYYYY)
   * @param fromPeriod - Optional range start (MMYYYY)
   * @param toPeriod - Optional range end (MMYYYY)
   */
  downloadExcel: async (
    category: string,
    returnPeriod?: string,
    fromPeriod?: string,
    toPeriod?: string
  ): Promise<void> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('category', category);
      if (returnPeriod) queryParams.append('return_period', returnPeriod);
      if (fromPeriod) queryParams.append('from_period', fromPeriod);
      if (toPeriod) queryParams.append('to_period', toPeriod);

      const response = await fetch(
        `${API_BASE_URL}/api/gstr2b/export-excel?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to download Excel');
      }

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `gstr2b_${category}_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Excel Download Error:', error);
      throw error;
    }
  },
};

export default gstr2bAPI;

